import { useState } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { 
  Building2, Phone, Mail, Globe, ExternalLink, 
  Edit2, Trash2, MoreVertical, Eye, EyeOff, Key, Users
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import type { TumUtilityVendor } from '@/types/tum';

interface VendorCardProps {
  vendor: TumUtilityVendor;
  onEdit: () => void;
  onDelete: () => void;
  onManageCredentials?: () => void;
}

export const VendorCard = ({ vendor, onEdit, onDelete, onManageCredentials }: VendorCardProps) => {
  const [showPassword, setShowPassword] = useState(false);

  const getInitials = (name: string) => {
    return name.split(' ').map(word => word[0]).join('').substring(0, 2).toUpperCase();
  };

  return (
    <Card className="p-3 hover:shadow-md transition-shadow">
      {/* Header */}
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-2.5">
          <Avatar className="h-10 w-10">
            <AvatarImage src={vendor.logo_url || ''} alt={vendor.vendor_name} />
            <AvatarFallback className="bg-primary/10 text-primary text-xs font-medium">
              {getInitials(vendor.vendor_name)}
            </AvatarFallback>
          </Avatar>
          <div className="min-w-0">
            <h3 className="font-medium text-sm text-foreground truncate">{vendor.vendor_name}</h3>
            {vendor.vendor_code && (
              <p className="text-[10px] text-muted-foreground">Code: {vendor.vendor_code}</p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-1">
          <Badge 
            variant={vendor.is_active !== false ? 'default' : 'secondary'} 
            className="text-[10px] px-1.5 py-0"
          >
            {vendor.is_active !== false ? 'Active' : 'Inactive'}
          </Badge>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" className="h-6 w-6 p-0">
                <MoreVertical className="h-3.5 w-3.5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={onEdit}>
                <Edit2 className="h-3.5 w-3.5 mr-2" />
                Edit
              </DropdownMenuItem>
              {onManageCredentials && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={onManageCredentials}>
                    <Users className="h-3.5 w-3.5 mr-2" />
                    Customer Credentials
                  </DropdownMenuItem>
                </>
              )}
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={onDelete} className="text-destructive">
                <Trash2 className="h-3.5 w-3.5 mr-2" />
                Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Contact Info */}
      <div className="space-y-1.5 mb-3">
        {vendor.customer_service_phone && (
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <Phone className="h-3 w-3 shrink-0" />
            <span className="truncate">{vendor.customer_service_phone}</span>
          </div>
        )}
        {vendor.customer_service_email && (
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <Mail className="h-3 w-3 shrink-0" />
            <span className="truncate">{vendor.customer_service_email}</span>
          </div>
        )}
        {vendor.website_url && (
          <div className="flex items-center gap-2 text-xs">
            <Globe className="h-3 w-3 shrink-0 text-muted-foreground" />
            <a 
              href={vendor.website_url} 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-primary hover:underline truncate"
            >
              {vendor.website_url.replace(/^https?:\/\//, '').split('/')[0]}
            </a>
          </div>
        )}
      </div>

      {/* Portal Info */}
      {vendor.portal_url && (
        <div className="bg-muted/50 rounded-md p-2 mb-3">
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-[10px] font-medium text-muted-foreground uppercase">Portal Access</span>
            <a 
              href={vendor.portal_url} 
              target="_blank" 
              rel="noopener noreferrer"
              className="flex items-center gap-1 text-[10px] text-primary hover:underline"
            >
              Open <ExternalLink className="h-2.5 w-2.5" />
            </a>
          </div>
          {vendor.portal_username && (
            <div className="flex items-center gap-1.5 text-xs">
              <span className="text-muted-foreground">User:</span>
              <span className="font-mono text-foreground">{vendor.portal_username}</span>
            </div>
          )}
          {vendor.portal_password_encrypted && (
            <div className="flex items-center gap-1.5 text-xs mt-0.5">
              <span className="text-muted-foreground">Pass:</span>
              <span className="font-mono text-foreground">
                {showPassword ? vendor.portal_password_encrypted : '••••••••'}
              </span>
              <button 
                onClick={() => setShowPassword(!showPassword)}
                className="p-0.5 hover:bg-muted rounded"
              >
                {showPassword ? (
                  <EyeOff className="h-3 w-3 text-muted-foreground" />
                ) : (
                  <Eye className="h-3 w-3 text-muted-foreground" />
                )}
              </button>
            </div>
          )}
        </div>
      )}

      {/* Security Questions Indicator */}
      {vendor.security_questions && Array.isArray(vendor.security_questions) && vendor.security_questions.length > 0 && (
        <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <Key className="h-3 w-3" />
          <span>{vendor.security_questions.length} security question(s)</span>
        </div>
      )}

      {/* Notes */}
      {vendor.notes && (
        <p className="text-xs text-muted-foreground mt-2 line-clamp-2 italic">
          {vendor.notes}
        </p>
      )}
    </Card>
  );
};
